import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiRepeat, FiPrinter, FiLink
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import LinkedDocNumbers, { parentLinks } from '../../components/LinkedDocNumbers';
import { getItemsCached, getCustomersCached, getAccountsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import { createTransaction, updateTransaction, deleteTransaction, getTransactions, updateTransactionStatus } from '../../utils/transactionHelper';
import {  } from '../../utils/subTransactionHelper';
import { invalidateItemStock } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import CustomerSelect from '../../components/CustomerSelect';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import { isFutureDate } from '../../utils/validationHelpers';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, PaymentSection,
  LinkedBadgeContainer, LinkedDocsSection, SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { recalcInvoiceReturns, getReturnedQtyByItem, capReturnableLines } from '../../utils/invoiceClearingHelper';
import { inActiveBranch } from '../../utils/branchLink';
import { carryEmployeeJob } from '../../utils/linkCarry';

const TRAN_TYPE_CODE = 'NSR';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted', 'Adjusted']
};

const EMPTY_LINE = {
  itemId: '',
  description: '',
  originalQty: 0,
  quantity: 0, // return quantity
  unitOfMeasure: '',
  unitCode: '',
  unitPrice: 0,
  unitCost: 0, // Cost price for COGS reversal
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  discountPercent: 0, // line discount % carried from the invoice line
  discountAmount: 0,  // computed = quantity * unitPrice * discountPercent/100
  lineTotal: 0,
  totalCost: 0, // quantity * unitCost for COGS reversal
  trackingType: 'None', batchNumber: '', expiryDate: '', serialNumbers: []
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Adjusted: colors.success.main,
};

// LinkedBadge component for document linking display
// Shows the linked documents' NUMBERS (not a count). `known` = numbers the
// document already carries (no lookup); the rest are looked up once per device.
const LinkedBadge = ({ label, ids, known, max = 3, icon: Icon = FiLink }) => {
  const list = (ids || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <LinkedBadgeContainer>
      <Icon size={12} />
      <span>{label}:</span>
      <LinkedDocNumbers ids={list} known={known} max={max} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
    </LinkedBadgeContainer>
  );
};

const SaleNonTaxReturn = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'nonTaxSaleReturn');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);


  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: returns,
    loading,
    refresh: loadReturns,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingReturn, setEditingReturn] = useState(null);
  const [viewingReturn, setViewingReturn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '',
    docDate: new Date().toISOString().split('T')[0],
    partyId: '',
    partyName: '',
    parentId: '', // Non-Tax Sale Invoice ID
    parentDocNumber: '',
    reason: '',
    notes: '',
    status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    // Footer (whole-invoice) discount carried from the source invoice, stored as a
    // RATE so it applies proportionally to whatever value is being returned.
    footerDiscountRate: 0,
    paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
    existingPaidAmount: 0
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [customers, setCustomers] = useState([]);
  const [postedInvoices, setPostedInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  // NOTE: no stockBalances here — this form has no item picker (lines come from
  // the parent invoice), so the old mount-time whole-history stock scan fed
  // nothing at all. Removed 2026-08 (Firebase cost).

  // Return list now comes from useDocListPagination above (loadReturns = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): customers/items/accounts come from the
  // device-local master cache (getMaster* → IndexedDB, 0 reads per open with a
  // throttled background change-check) instead of a full getDocs on every open.
  // Each screen keeps its own client-side filter/sort on the returned rows.
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers([...rows].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) { console.error('Error loading customers:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the loader below feeds ONLY the form's
  // "Link to Invoice" dropdown — nothing on the list view uses it. It used to
  // run on every page open AND after every save/delete, scanning full invoice
  // history (every Posted + Partial + Paid NSI ever). It now loads ONCE, lazily,
  // the first time a form is actually opened. List browsing costs nothing.
  const formDataLoadedRef = useRef(false);

  const loadPostedInvoices = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Firebase cost: scope the parent-invoice list to the ACTIVE financial year
      // (returns are normally filed against this year's invoices). A prior-year
      // invoice won't appear in the picker — rare, and a return can still be
      // created from the invoice itself if needed.
      const fyScope = activeFinancialYear?.id ? { financialYearId: activeFinancialYear.id } : {};
      const postedNSIs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'NSI', status: 'Posted', ...fyScope
      });
      const partialNSIs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'NSI', status: 'Partial', ...fyScope
      });
      const paidNSIs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'NSI', status: 'Paid', ...fyScope
      });
      setPostedInvoices([...postedNSIs, ...partialNSIs, ...paidNSIs]);
    } catch (error) { console.error('Error loading invoices:', error); }
  }, [activeCompany, activeFinancialYear]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadPostedInvoices()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadPostedInvoices]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadPaymentAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const allAccts = await getAccountsCached(activeCompany.id);
      const allLvl5 = allAccts.filter(a => a.level === 5 || a.isPostable);
      setPaymentAccounts(allLvl5.filter(a => a.code?.startsWith('11101') || a.code?.startsWith('11102')));
    } catch (error) { console.error('Error loading payment accounts:', error); }
  }, [activeCompany]);

  // NOTE: the return list itself loads inside useDocListPagination — not here.
  // D3: the invoice loader is NOT here anymore — it loads lazily on first form
  // open (ensureFormData), since only the form uses it.
  useEffect(() => {
    loadCustomers(); loadItems();
    loadEmployees(); loadJobs(); loadLocations(); loadPaymentAccounts();
  }, [loadCustomers, loadItems, loadEmployees, loadJobs, loadLocations, loadPaymentAccounts]);

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingReturn(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Filter
  const filteredReturns = useMemo(() => {
    let filtered = returns;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) ||
        r.partyName?.toLowerCase().includes(q) ||
        r.parentDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [returns, statusFilter, searchQuery, sort]);

  // Calculate line (including totalCost for COGS reversal). Non-tax: taxAmount always 0.
  // Mirrors the non-tax invoice: gross minus the line discount %, so the return
  // credits the same net the invoice charged.
  const calculateLine = (line) => {
    const gross = line.quantity * line.unitPrice;
    const discAmt = gross * ((Number(line.discountPercent) || 0) / 100);
    const net = gross - discAmt;
    const totalCost = (line.quantity || 0) * (line.unitCost || 0);
    return {
      ...line,
      taxable: false,
      taxRate: 0,
      taxAmount: 0,
      discountAmount: Math.round(discAmt * 100) / 100,
      lineTotal: Math.round(net * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100
    };
  };

  // Totals (non-tax: taxAmount always 0)
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    // Subtotal is AFTER per-line discounts (same as the invoice).
    const subtotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - (l.discountAmount || 0)), 0);
    // Footer discount = invoice's whole-invoice discount, applied proportionally
    // to the value being returned (footerDiscountRate captured at invoice load).
    const footerDiscount = Math.round(subtotal * (Number(formData.footerDiscountRate) || 0) * 100) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      footerDiscount,
      taxAmount: 0,
      totalAmount: Math.round((subtotal - footerDiscount) * 100) / 100
    };
  }, [lineItems, formData.footerDiscountRate]);

  // Form handlers
  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    // Auto-fill the salesperson (employee) from the customer's saved default.
    const spPatch = customer?.salesPersonId
      ? { employeeId: customer.salesPersonId, employeeName: customer.salesPersonName || '' }
      : {};
    setFormData(prev => ({
      ...prev, partyId: customerId, partyName: customer?.companyName || '',
      parentId: '', parentDocNumber: '', footerDiscountRate: 0, ...spPatch
    }));
    setLineItems([{ ...EMPTY_LINE }]);
  };

  const handleInvoiceChange = (parentId) => {
    const inv = postedInvoices.find(i => i.id === parentId);
    if (!inv) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', footerDiscountRate: 0 }));
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    // ACC-11: cap each line at what is still returnable (invoice qty minus
    // quantities already returned on other live returns of this invoice).
    getReturnedQtyByItem(activeCompany.id, parentId, editingReturn?.id || null)
      .then(returnedByItem => {
        if (Object.keys(returnedByItem).length > 0) setLineItems(prev => capReturnableLines(prev, returnedByItem));
      })
      .catch(err => console.error('Could not load already-returned quantities:', err));
    // Footer discount as a rate of the invoice's after-line-discount value, so it
    // scales to whatever portion is returned (full or partial).
    const invBase = (inv.lines || []).reduce((s, l) => s + ((l.quantity || 0) * (l.unitPrice || 0) - (Number(l.discountAmount) || 0)), 0);
    const footerDiscountRate = invBase > 0 ? (Number(inv.discountAmount) || 0) / invBase : 0;
    setFormData(prev => ({
      ...prev, parentId,
      parentDocNumber: inv.docNumber,
      partyId: inv.partyId, partyName: inv.partyName,
      footerDiscountRate,
      ...carryEmployeeJob(inv)
    }));
    // Auto-fill lines from invoice (including cost for COGS reversal)
    const invLines = inv.lines || [];
    if (invLines.length > 0) {
      setLineItems(invLines.map(l => {
        const item = items.find(i => i.id === l.itemId);
        return {
          ...EMPTY_LINE,
          itemId: l.itemId || '',
          description: l.description || '',
          originalQty: l.quantity || 0,
          quantity: 0, // return qty starts at 0
          unitOfMeasure: l.unitOfMeasure || '',
          unitPrice: l.unitPrice || 0,
          unitCost: l.unitCost || 0, // Cost price from invoice line for COGS reversal
          discountPercent: Number(l.discountPercent) || 0, // carry line discount from invoice
          discountAmount: Number(l.discountAmount) || 0,   // recomputed by calculateLine on return qty
          taxable: false,
          taxRate: 0,
          trackingType: item?.trackingType || l.trackingType || 'None',
          batchNumber: '',
          expiryDate: '',
          serialNumbers: []
        };
      }));
    }
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Size-mode line edits: recompute Total SQft when L / H / Thickness / Pieces / sizeUnit change.
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
      }

      // When item is selected manually, look up tracking type
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].taxRate = 0;
          updated[index].taxable = false;
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].expiryDate = '';
          updated[index].serialNumbers = [];
          const seeded = seedLineUnitFromItem(item, { priceField: 'salePrice', defaultUnitField: 'defaultSaleUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure };

          // Size mode: prime with item defaults and force unit to SQft.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
          } else {
            updated[index].hasSize = false;
          }
        }
      }
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'salePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure };
        }
      }
      // Validate return qty doesn't exceed original
      if (field === 'quantity' && updated[index].originalQty > 0) {
        updated[index].quantity = Math.min(value, updated[index].originalQty);
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || !!line.description || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', parentId: '', parentDocNumber: '',
      reason: '', notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '',
      paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
      existingPaidAmount: 0
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingReturn(null);
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (ret) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load invoice link data on demand
    setEditingReturn(ret);
    setFormData({
      docNumber: ret.docNumber || '',
      docDate: ret.docDate || new Date().toISOString().split('T')[0],
      partyId: ret.partyId || '', partyName: ret.partyName || '',
      parentId: ret.parentId || '',
      parentDocNumber: ret.parentDocNumber || '',
      reason: ret.reason || '', notes: ret.notes || '', status: ret.status || 'Draft',
      employeeId: ret.employeeId || '', employeeName: ret.employeeName || '',
      jobId: ret.jobId || '', jobName: ret.jobName || '',
      locationId: ret.locationId || '', locationName: ret.locationName || '',
      // Restore the footer-discount rate; fall back to deriving it from an older
      // saved return that only stored the flat discountAmount + subtotal.
      footerDiscountRate: ret.footerDiscountRate ?? ((Number(ret.subtotal) || 0) > 0 ? (Number(ret.discountAmount) || 0) / ret.subtotal : 0),
      paymentAmount: ret.paymentAmount || 0, paymentAccountId: ret.paymentAccountId || '', paymentAccountName: ret.paymentAccountName || '',
      existingPaidAmount: Math.max(0, (ret.paidAmount || 0) - (ret.paymentAmount || 0))
    });
    setLineItems(ret.lines?.length > 0 ? ret.lines.map(l => ({ ...EMPTY_LINE, ...l, taxable: false, taxRate: 0, taxAmount: 0 })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };

  const handleView = (ret) => { setViewingReturn(ret); setViewMode('view'); };

  // Save return
  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a customer'); return; }
    if (!formData.parentId) { alert('Please select a non-tax sale invoice'); return; }
    if (!formData.reason.trim()) { alert('Please enter a reason for return'); return; }
    if (Number(formData.paymentAmount) > 0 && !formData.paymentAccountId) {
      alert('Please select a refund account'); return;
    }
    const validLines = lineItems.filter(l => l.description && l.quantity > 0);
    if (validLines.length === 0) {
      alert('Please add at least one line with return quantity > 0');
      return;
    }

    // Validate financial year
    if (!activeFinancialYear?.id) {
      alert('No active financial year found. Please set an active financial year in Settings > Financial Year.');
      return;
    }

    // Validate batch/serial tracking
    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch number is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const qty = line.quantity || 0;
        if (serials.length !== qty) {
          alert(`"${line.itemName || line.description}" requires exactly ${qty} serial number(s). You entered ${serials.length}.`); return;
        }
        if (new Set(serials).size !== serials.length) {
          alert(`Duplicate serial numbers found for "${line.itemName || line.description}"`); return;
        }
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => ({ ...calculateLine(l), lineNumber: i + 1 }));
      const status = saveStatus || formData.status || 'Draft';

      const retData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        partyType: 'customer',
        partyId: formData.partyId,
        partyName: formData.partyName,
        parentId: formData.parentId,
        parentDocNumber: formData.parentDocNumber,
        reason: formData.reason,
        notes: formData.notes,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        subtotal: totals.subtotal,
        discountAmount: totals.footerDiscount, // footer discount (proportional to returned value)
        footerDiscountRate: Number(formData.footerDiscountRate) || 0, // persisted so edits re-proportion correctly
        taxAmount: 0,
        totalAmount: totals.totalAmount,
        paymentAmount: Number(formData.paymentAmount) || 0,
        paymentAccountId: formData.paymentAccountId || null,
        paymentAccountName: formData.paymentAccountName || null,
        paidAmount: (Number(formData.existingPaidAmount) || 0) + (Number(formData.paymentAmount) || 0),
        balanceAmount: totals.totalAmount - (Number(formData.existingPaidAmount) || 0) - (Number(formData.paymentAmount) || 0),
        status,
        lines: calculatedLines,
        financialYearId: activeFinancialYear.id
      };

      if (status === 'Posted' && formData.status !== 'Posted') {
        retData.postedBy = currentUser.uid;
        retData.postedAt = new Date().toISOString();
      }

      let savedDocId;
      let savedDocNumber;
      let newTranData = null;
      if (editingReturn) {
        await updateTransaction(activeCompany.id, editingReturn.id, retData, currentUser.uid);
        savedDocId = editingReturn.id;
        savedDocNumber = editingReturn.docNumber;
      } else {
        newTranData = await createTransaction(activeCompany.id, retData, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      // SubTransactions + StockFlow already generated by createTransaction/updateTransaction above.
      // No duplicate calls needed here.


      // ACC-11 (2026-09-30): the parent invoice's returned amount / balance /
      // status is RE-COMPUTED from all its live returns (never incremented), so
      // saving, re-posting or editing a return can't double-count, and returns
      // are no longer booked as cash "paid" on the invoice.
      {
        const parentsToRecalc = new Set([formData.parentId, editingReturn?.parentId].filter(Boolean));
        for (const pid of parentsToRecalc) {
          try { await recalcInvoiceReturns(activeCompany.id, pid); } catch (err) { console.error('Error updating parent invoice:', err); }
        }
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingReturn
        ? { ...editingReturn, ...retData, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingReturn
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved back in — drop the per-item cached quantities so the next
      // "stock?" lookup on any form this session reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // D3: only the (paginated) return list is refreshed from the server — the
      // invoice link data is form-only and stays in memory.
      // Background refresh — reconciles with server data without blocking UI.
      loadReturns().catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving non-tax sale return:', error);
      alert('Error saving non-tax sale return: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ret) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${ret.docNumber}?`)) return;
    try {
      // ACC-04: deleteTransaction() checks first, then removes ledger + stock rows.
      await deleteTransaction(activeCompany.id, ret.id);
      // ACC-11: refresh the parent invoice from its remaining live returns.
      if (ret.parentId) {
        try { await recalcInvoiceReturns(activeCompany.id, ret.parentId); } catch (err) { console.error('Error reversing parent invoice balance:', err); }
      }
      // D3: parent invoice balances/statuses changed — mark the form link data
      // stale so the NEXT form open refetches it, instead of paying for it here
      // on the list view.
      formDataLoadedRef.current = false;
      await loadReturns();
    } catch (error) { console.error('Error deleting return:', error); alert('Could not delete: ' + (error?.message || 'unknown error')); }
  };

  // Print
  const handlePrint = (ret) => {
    const target = ret || viewingReturn;
    const cust = customers.find(c => c.id === target?.partyId);
    const enrichedLines = (target?.lines || []).map(l => {
      const it = items.find(i => i.id === l.itemId);
      return { ...l, imageUrl: it?.imageUrl || l.imageUrl || '' };
    });
    const enriched = { ...target, lines: enrichedLines, partyNTN: cust?.ntn || target?.partyNTN || '', partySTRN: cust?.strn || target?.partySTRN || '' };
    printDocument('nonTaxSaleReturn', enriched, activeCompany, printConfig, activeBranch);
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Posted') : undefined,
    viewMode,
    saving
  });

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by return#, customer, invoice#..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Adjusted">Adjusted</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Return</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading non-tax sale returns...</LoadingMessage>
      ) : filteredReturns.length === 0 ? (
        <TranEmptyState>
          <FiRepeat size={48} />
          <h3>No Non-Tax Sale Returns</h3>
          <p>{returns.length === 0 ? 'Create your first non-tax sale return' : 'No returns match your filters'}</p>
          {returns.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Return</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">Return #</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Customer</SortableTh>
                <SortableTh field="parentDocNumber">Invoice Ref</SortableTh>
                <SortableTh field="reason">Reason</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Amount</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Links</Th>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredReturns.map(ret => (
                <Tr key={ret.id}>
                  <Td><strong>{ret.docNumber}</strong></Td>
                  <Td>{ret.docDate}</Td>
                  {showBranchCol && <Td>{ret.branchName || '-'}</Td>}
                  <Td>{ret.partyName}</Td>
                  <Td>{ret.parentDocNumber || '-'}</Td>
                  <Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ret.reason || '-'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {activeCompany?.currencyCode || 'PKR'} {formatAmount(ret.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[ret.status]}>{ret.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <LinkedBadge label="NSI" {...parentLinks(ret, 'NSI')} />
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(ret)}><FiEye /></IconBtn>
                      {canChange && <IconBtn title="Edit" onClick={() => handleEdit(ret)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(ret)}><FiPrinter /></IconBtn>
                      {ret.status !== 'Draft' && ret.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="nonTaxSaleReturn" doc={ret} party={customers.find(c => c.id === ret.partyId)} variant="icon" />
                      )}
                      {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(ret)}><FiTrash2 /></IconBtn>}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredReturns.length : pagination.totalCount}</strong> return(s)</span>
        <span>
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Posted: <strong>{listSummary.counts.Posted || 0}</strong> |{' '}
          Adjusted: <strong>{listSummary.counts.Adjusted || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingReturn) return null;
    const ret = viewingReturn;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingReturn(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {ret.docNumber}
            <StatusBadge $color={STATUS_COLORS[ret.status]} style={{ marginLeft: 12 }}>{ret.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(ret)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(ret)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Return Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Return Number</label><span>{ret.docNumber}</span></InfoItem>
            <InfoItem><label>Return Date</label><span>{ret.docDate}</span></InfoItem>
            <InfoItem><label>Customer</label><span>{ret.partyName}</span></InfoItem>
            <InfoItem><label>Original Invoice</label><span>{ret.parentDocNumber || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{ret.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{ret.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{ret.locationName || '-'}</span></InfoItem>
            <InfoItem style={{ gridColumn: 'span 2' }}><label>Reason for Return</label><span>{ret.reason}</span></InfoItem>
          </InfoGrid>
          {ret.parentId && (
            <LinkedDocsSection>
              <LinkedBadge label="Linked NSI" {...parentLinks(ret, 'NSI')} max={0} />
            </LinkedDocsSection>
          )}
        </FormCard>

        <FormCard>
          <SectionTitle>Return Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Orig. Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Return Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(ret.lines || []).map((line, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.originalQty || '-'}</Td>
                    <Td style={{ textAlign: 'right', color: colors.error.main, fontWeight: 500 }}>{line.quantity}{line.unitOfMeasure ? <span style={{ fontSize: '11px', color: '#64748B', marginLeft: '4px' }}>{line.unitOfMeasure}</span> : ''}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 500 }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(ret.subtotal)}</td></tr>
                {ret.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(ret.discountAmount)}</td></tr>}
                <TotalRow><td>Total Return</td><td>{activeCompany?.currencyCode || 'PKR'} {formatAmount(ret.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        {ret.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{ret.notes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={ret?.id} attachment={ret?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => {
    // Only this branch's invoices (see utils/branchLink.js).
    const customerInvoices = postedInvoices.filter(inv => !formData.partyId || inv.partyId === formData.partyId)
      .filter(inv => inActiveBranch(inv, activeBranch, formData.parentId));

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>{editingReturn ? `Edit: ${editingReturn.docNumber}` : 'New Non-Tax Sale Return'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
              <FiX /> Cancel
            </SaveButton>
            {canEdit && !(editingReturn && editingReturn.status && editingReturn.status !== 'Draft' && editingReturn.status !== 'Cancelled') && (
              <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
              </SaveButton>
            )}
            {canEdit && (formData.status === 'Draft' || !editingReturn) && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}>
                <FiCheck /> Post Return
              </SaveButton>
            )}
            {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
            {canEdit && (editingReturn && editingReturn.status && editingReturn.status !== 'Draft' && editingReturn.status !== 'Cancelled') && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Return Details</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Return Number</Label>
              <Input value={editingReturn?.docNumber || 'Auto-generated'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Return Date *</Label>
              <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input value={formData.status} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Customer *</Label>
              <CustomerSelect customers={customers} selectedCustomerId={formData.partyId} onChange={handleCustomerChange} disabled={!!formData.parentId} balanceOnDemand />
            </FormGroup>
            <FormGroup $span2>
              <Label>Non-Tax Sale Invoice *</Label>
              <Select value={formData.parentId} onChange={e => handleInvoiceChange(e.target.value)}>
                <option value="">-- Select Invoice --</option>
                {customerInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.docNumber} - {inv.partyName} ({activeCompany?.currencyCode || 'PKR'} {formatAmount(inv.totalAmount)})
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Employee</Label>
              <Select value={formData.employeeId} onChange={e => {
                const emp = employees.find(em => em.id === e.target.value);
                handleHeaderChange('employeeId', e.target.value);
                handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
              }}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Job</Label>
              <Select value={formData.jobId} onChange={e => {
                const job = jobs.find(j => j.id === e.target.value);
                handleHeaderChange('jobId', e.target.value);
                handleHeaderChange('jobName', job?.jobName || '');
              }}>
                <option value="">-- Select Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>{reqLabel(activeCompany, 'Location')}</Label>
              <Select value={formData.locationId} onChange={e => {
                const loc = locations.find(l => l.id === e.target.value);
                handleHeaderChange('locationId', e.target.value);
                handleHeaderChange('locationName', loc?.locationName || '');
              }}>
                <option value="">-- Select Location --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.locationName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup $span2>
              <Label>Reason for Return *</Label>
              <Input value={formData.reason} onChange={e => handleHeaderChange('reason', e.target.value)}
                placeholder="e.g., Damaged goods, wrong items, quality issue..." />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Return Items</SectionTitle>
            {!formData.parentId && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
          </LineItemsHeader>

          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                  <LineTh style={{ width: 300, minWidth: 300, maxWidth: 300 }}>Description</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Orig. Qty</LineTh>
                  <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Return Qty</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                  <LineTh style={{ width: 90, minWidth: 90, maxWidth: 90 }}>Disc %</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Batch #</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Expiry Date</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => {
                  const calc = calculateLine(line);
                  return (
                    <React.Fragment key={index}><tr>
                      <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                      <LineTd>
                        <LineInput
                          ref={(el) => { lineItemRefs.current[index] = el; }}
                          value={line.description}
                          onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Item description" disabled={!!formData.parentId} />
                        
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" value={line.originalQty} disabled
                          style={{ textAlign: 'right', background: colors.neutral[50] }} />
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LineInput type="number" min="0" max={line.originalQty || 999999} value={line.quantity}
                            onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))}
                            disabled={line.hasSize}
                            title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                            style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined }} />
                          <LineUnitPicker
                            item={items.find(i => i.id === line.itemId)}
                            line={line}
                            onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                          />
                        </div>
                        <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                          quantity={line.quantity} hasSize={line.hasSize}
                          onChange={(q) => handleLineChange(index, 'quantity', q)} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" step="0.01" value={line.unitPrice}
                          onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))}
                          onKeyDown={(e) => handleLastFieldEnter(e, index)}
                          style={{ textAlign: 'right' }} disabled={!!formData.parentId} />
                      </LineTd>
                      <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>
                        {line.discountPercent ? `${line.discountPercent}%` : '-'}
                      </LineTd>
                      <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>
                          {line.trackingType === 'Batch' ? (
                            <LineInput value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} placeholder="Batch #" />
                          ) : <span style={{ color: colors.text.secondary }}>-</span>}
                        </LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>
                          {line.trackingType === 'Batch' ? (
                            <LineInput type="date" value={line.expiryDate || ''} onChange={e => handleLineChange(index, 'expiryDate', e.target.value)} />
                          ) : <span style={{ color: colors.text.secondary }}>-</span>}
                        </LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Serial') && (
                        <LineTd>
                          {line.trackingType === 'Serial' ? (
                            <LineInput value={line.serialText !== undefined ? line.serialText : (line.serialNumbers || []).join(', ')}
                              onChange={e => handleLineChange(index, 'serialText', e.target.value)}
                              onBlur={e => { const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean); handleLineChange(index, 'serialNumbers', parsed); handleLineChange(index, 'serialText', undefined); }}
                              placeholder={`Enter ${line.quantity || 0} serial(s), comma-separated`} />
                          ) : <span style={{ color: colors.text.secondary }}>-</span>}
                        </LineTd>
                      )}
                      <LineTd style={{ textAlign: 'center' }}>
                        {!formData.parentId && lineItems.length > 1 && (
                          <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>
                        )}
                      </LineTd>
                    </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={!!formData.parentId} leftColSpan={1} />)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LineTable>
          </LineTableContainer>

          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
                {totals.footerDiscount > 0 && <tr><td>Discount</td><td>-{formatAmount(totals.footerDiscount)}</td></tr>}
                <TotalRow><td>Total Return</td><td>{activeCompany?.currencyCode || 'PKR'} {formatAmount(totals.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>

          <PaymentSection>
            <SectionTitle>Refund</SectionTitle>
            <FormGrid>
              {formData.existingPaidAmount > 0 && (
                <FormGroup>
                  <Label>Refunded via Vouchers</Label>
                  <Input value={formatAmount(formData.existingPaidAmount)} disabled
                    style={{ fontWeight: 600, color: colors.success.main }} />
                </FormGroup>
              )}
              <FormGroup>
                <Label>Refund Amount</Label>
                <Input type="number" min="0" step="0.01" value={formData.paymentAmount}
                  onChange={e => handleHeaderChange('paymentAmount', Math.min(Number(e.target.value), totals.totalAmount - formData.existingPaidAmount))}
                  placeholder="0.00" />
              </FormGroup>
              <FormGroup>
                <Label>Refund Account</Label>
                <Select value={formData.paymentAccountId} onChange={e => {
                  const acc = paymentAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('paymentAccountId', e.target.value);
                  handleHeaderChange('paymentAccountName', acc?.name || '');
                }}>
                  <option value="">-- Select Account --</option>
                  <optgroup label="Cash Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11101')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bank Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11102')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                </Select>
              </FormGroup>
              {(formData.existingPaidAmount > 0 || Number(formData.paymentAmount) > 0) && (
                <FormGroup>
                  <Label>Balance After Refund</Label>
                  <Input value={formatAmount(totals.totalAmount - formData.existingPaidAmount - (Number(formData.paymentAmount) || 0))} disabled
                    style={{ fontWeight: 600, color: colors.error.main }} />
                </FormGroup>
              )}
            </FormGrid>
          </PaymentSection>
        </FormCard>

        <FormCard>
          <SectionTitle>Additional Information</SectionTitle>
          <FormGrid>
            <FormGroup $span2>
              <Label>Notes</Label>
              <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <AttachmentPanel
          transactionId={editingReturn?.id}
          attachment={editingReturn?.attachment}
          canEdit={canEdit}
          onChange={({ attachment }) => setEditingReturn(prev => prev ? { ...prev, attachment } : prev)}
        />
      </>
    );
  };

  return (
    <PageLayout pageTitle="Non-Tax Sale Return (Credit Note)" breadcrumb="Sales / Non-Tax Sale Return">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default SaleNonTaxReturn;
